import {
  SkyBadge,
  SkyButton,
  SkyDialog,
  SkyDrawer,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useState, type ReactNode } from "react";
import {
  TENANT_RESOURCE_STEP_LABELS,
  provisionerErrorCodeSchema,
  type ProvisionerErrorCode,
  type TenantProvisioningJob,
  type TenantResourceTargetView,
} from "@shared/types/tenantResources";
import type { TenantView } from "@shared/types/tenants";
import {
  ProvisioningPanel,
  type ProvisioningPanelSource,
} from "@/components/platform/ProvisioningPanel";
import { CHIP_COLOR, resourcesChipState } from "@/components/platform/TenantResourcesChip";
import {
  useApplyTenantResources,
  useRetryTenantResources,
  useTenantProvisioning,
  useTenantResourceTargets,
} from "@/hooks/use-tenant-provisioning";
import { usePermissions } from "@/hooks/use-permissions";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { ApiClientError } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { formatInstant } from "@/lib/time/displayTime";
import { ErrorState } from "./ScreenState";

// copy-jargon-file-ok: a tenant's Azure resources, for Skypoint support; the drawer is about them. (#929, scripts/check-copy.mjs)

/**
 * Tenants > Resources (ADR 0010, design §7.5): what a tenant has in Azure and how far its
 * provisioning is. Two steppers, "Database" (the tenant's database job) and "Azure resources" (the
 * provisioner's), both read from `GET /platform/tenants/:id/provisioning`, which re-reads every
 * 3 s while a step is running or pending and then stops. A failed step shows its closed code in
 * a plain sentence and, for a holder of `platform.tenants.manage`, a Retry.
 *
 * "Apply now" (F31) deploys a per-tenant tenant's template again when it is active or failed and
 * no job is queued or running: Retry only requeues a FAILED newest job, so a healthy tenant needs
 * its own button (the runbook's package-2 order applies each tenant prepared before R-J). It asks
 * first. An active tenant's names never change; a failed one was never ready, so an apply may move
 * to new names on a collision, as a Retry may.
 *
 * The drawer shows names and ids only: no key, no connection string, no SAS.
 */

export const RETRY_CONFLICT_SENTENCE = "A job for this tenant is already queued or running.";

/** The confirm for an ACTIVE tenant: its names are fixed once it is ready. */
export const APPLY_CONFIRM_SENTENCE =
  "Apply the tenant's Azure resources template again? Its resources and names stay as they are.";

/**
 * The confirm for a FAILED tenant: it was never ready, so its names are not fixed yet, and an
 * apply that finds a name taken moves to the next one, as a Retry does.
 */
export const APPLY_CONFIRM_FAILED_SENTENCE =
  "Apply the tenant's Azure resources template again? If one of its names is already taken in Azure, the apply can choose new names, as Retry can.";

/**
 * Apply now is offered to a holder of `platform.tenants.manage` for an enabled per-tenant tenant
 * whose resources are active or failed, once the jobs are read and none is queued or running.
 * The route refuses every other case with 409 anyway.
 */
export function canApplyNow(
  tenant: TenantView,
  resourcesJob: TenantProvisioningJob | undefined,
  jobsRead: boolean,
): boolean {
  const resources = tenant.resources;
  if (!resources || resources.mode === "shared" || tenant.status === "disabled") return false;
  if (resources.status !== "active" && resources.status !== "failed") return false;
  if (!jobsRead) return false;
  return resourcesJob?.status !== "queued" && resourcesJob?.status !== "running";
}

/** A plain sentence for each of the provisioner's closed error codes (design §4.2). */
export const PROVISIONER_ERROR_SENTENCES: Record<ProvisionerErrorCode, string> = {
  ARM_NOT_AVAILABLE: "Skypoint could not reach Azure Resource Manager from this deployment.",
  ENV_INVALID: "This deployment's settings for tenant resources are incomplete or invalid.",
  NAME_COLLISION:
    "A resource with one of this tenant's names already exists and does not belong to this tenant.",
  NAME_EXHAUSTED:
    "Every name for this tenant is taken (generations 1 to 9). Ask Skypoint support: one possible cause is a deleted vault or account with one of this tenant's names that Azure still holds in soft delete. Wait for its retention to end (or purge it, if purge protection is off), then Retry.",
  WHAT_IF_REFUSED:
    "The plan would have changed or deleted something it should not, so nothing was deployed.",
  DEPLOY_FAILED: "Azure refused part of the deployment.",
  DEPLOY_TIMEOUT: "The deployment did not finish in time.",
  POLICY_DENIED: "An Azure policy on the target refused one of the resources.",
  QUOTA_EXCEEDED: "The target subscription has no quota left for one of the resources.",
  OUTPUT_MISMATCH: "What Azure created does not match what was planned.",
  DNS_PUBLIC: "A private name resolves to a public address, so the job stopped before binding.",
  GRANT_UNEXPECTED: "A role grant on the tenant's resources is missing or wider than expected.",
  ARM_SCOPE_LOCKED:
    "An Azure lock on the tenant's resources stopped this change. Ask your Azure administrator to remove the lock, then Retry.",
  TRANSIENT: "A temporary Azure error stopped the job. A retry usually clears it.",
  JOB_TENANT_MISMATCH:
    "The job and the tenant do not agree, so it stopped without changing anything.",
  TARGET_NOT_REGISTERED: "The tenant's placement target is no longer registered.",
  TARGET_MISMATCH: "The tenant's resources are not on the target recorded for it.",
};

/** The sentence for a code; a code outside the closed set (a database job's own) says so plainly. */
export function errorCodeSentence(code: string): string {
  const known = provisionerErrorCodeSchema.safeParse(code);
  return known.success
    ? PROVISIONER_ERROR_SENTENCES[known.data]
    : "The job stopped with a code this screen has no words for. Quote it to Skypoint support.";
}

/** "East US 2 (Contoso production)": the target's name and where it is. */
export function targetOptionText(target: TenantResourceTargetView): string {
  return `${target.displayName} (${target.location})`;
}

const MODE_WORDS = {
  shared: "The deployment's shared storage and vault",
  per_tenant: "Its own Azure resources",
  byo_vault: "Its own vault",
} as const;

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt>
        <SkyText size={200} color="muted" block>
          {label}
        </SkyText>
      </dt>
      <dd className="m-0 min-w-0">
        <SkyText size={300} block className="break-all">
          {children}
        </SkyText>
      </dd>
    </div>
  );
}

function asPanelSource(
  query: ReturnType<typeof useTenantProvisioning>,
  job: TenantProvisioningJob | undefined,
  message: string | null,
): ProvisioningPanelSource {
  return {
    data: job ? { id: job.id, status: job.status, steps: job.steps, error: message } : undefined,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

function ResourceFacts({ tenant }: { tenant: TenantView }) {
  const zone = useDisplayTz();
  const { can } = usePermissions();
  // The registered targets are a PlatformAdmin's read; without it the region line is left out.
  const targets = useTenantResourceTargets(can("platform.tenants.manage"));
  const resources = tenant.resources;
  if (!resources) return null;
  const target = resources.target;
  const region = targets.data?.targets.find((entry) => entry.id === target?.id)?.location;
  const state = resourcesChipState(resources);
  return (
    <div className="flex flex-col gap-4">
      <dl className="m-0 grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
        <Fact label="Status">
          <SkyBadge
            appearance="tint"
            color={CHIP_COLOR[state]}
            testId="tenant-resources-drawer-status"
          >
            {state}
          </SkyBadge>
        </Fact>
        <Fact label="Uses">{MODE_WORDS[resources.mode]}</Fact>
        <Fact label="Key Vault">
          <span className="font-mono">{resources.keyVaultName}</span>
        </Fact>
        {resources.storageAccountName ? (
          <Fact label="Storage account">
            <span className="font-mono">{resources.storageAccountName}</span>
          </Fact>
        ) : null}
        {resources.readyAt ? (
          <Fact label="Ready">
            {formatInstant(resources.readyAt, zone, "short", { named: true })}
          </Fact>
        ) : null}
        {resources.mode === "per_tenant" ? (
          <Fact label="Defender for Storage">
            {resources.defenderForStorageEffective ? "On" : "Off"}
            {resources.defenderForStorage === null ? " (the target's default)" : ""}
          </Fact>
        ) : null}
      </dl>
      {target ? (
        <div
          className="flex flex-col gap-2 border-t border-border pt-3"
          data-testid="tenant-resources-target"
        >
          <SkyText as="h3" size={300} weight="semibold" block>
            Placement target
          </SkyText>
          <dl className="m-0 grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
            <Fact label="Target">{target.displayName}</Fact>
            {region ? <Fact label="Region">{region}</Fact> : null}
            <Fact label="Subscription">
              <span className="font-mono">{target.subscriptionId}</span>
            </Fact>
            <Fact label="Resource group">
              <span className="font-mono">{target.resourceGroup}</span>
            </Fact>
          </dl>
        </div>
      ) : null}
    </div>
  );
}

function FailureBody({ code }: { code: string }) {
  return (
    <span>
      <span className="font-mono">{code}</span>. {errorCodeSentence(code)}
    </span>
  );
}

function Steppers({ tenant }: { tenant: TenantView }) {
  const provisioning = useTenantProvisioning(tenant.id);
  const retry = useRetryTenantResources();
  const apply = useApplyTenantResources();
  const { can } = usePermissions();
  const notice = useNotice();
  const retryError = useInlineError();
  const applyError = useInlineError();
  const [conflict, setConflict] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);

  const jobs = provisioning.data?.jobs ?? [];
  const databaseJob = jobs.find((job) => job.kind === "tenant_db");
  const resourcesJob = jobs.find((job) => job.kind === "tenant_resources");
  const perTenant = tenant.resources?.mode !== "shared";

  function retryResources() {
    setConflict(false);
    retryError.clear();
    retry.mutate(tenant.id, {
      onSuccess: () => notice.success("Retry queued"),
      onError: (error) => {
        if (error instanceof ApiClientError && error.status === 409) setConflict(true);
        else retryError.show(error, "The retry was not queued.");
      },
    });
  }

  function applyNow() {
    setConflict(false);
    applyError.clear();
    apply.mutate(tenant.id, {
      onSuccess: () => {
        setConfirmApply(false);
        notice.success("Apply queued");
      },
      onError: (error) => {
        setConfirmApply(false);
        if (error instanceof ApiClientError && error.status === 409) setConflict(true);
        else applyError.show(error, "The apply was not queued.");
      },
    });
  }

  const showApplyNow =
    can("platform.tenants.manage") &&
    canApplyNow(tenant, resourcesJob, provisioning.data !== undefined);

  const retryButton =
    resourcesJob?.status === "failed" && can("platform.tenants.manage") ? (
      <SkyButton
        appearance="primary"
        size="small"
        loading={retry.isPending}
        onClick={retryResources}
        testId="tenant-resources-retry"
      >
        Retry
      </SkyButton>
    ) : undefined;

  if (provisioning.isError && provisioning.data === undefined) {
    return (
      <ErrorState
        title="The provisioning steps did not load"
        error={provisioning.error}
        onRetry={() => void provisioning.refetch()}
        testId="tenant-provisioning-error"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {conflict ? (
        <SkyMessageBar
          intent="warning"
          message={RETRY_CONFLICT_SENTENCE}
          onDismiss={() => setConflict(false)}
          testId="tenant-resources-retry-conflict"
        />
      ) : null}
      <InlineErrorBar
        error={retryError.error}
        title="The retry was not queued"
        onDismiss={retryError.clear}
        testId="tenant-resources-retry-error"
      />
      <InlineErrorBar
        error={applyError.error}
        title="The apply was not queued"
        onDismiss={applyError.clear}
        testId="tenant-resources-apply-error"
      />
      <div data-testid="tenant-stepper-database">
        <ProvisioningPanel
          instanceId={null}
          instanceName={tenant.displayName}
          title="Database"
          headingLevel="h3"
          testId="tenant-provisioning-database"
          source={asPanelSource(provisioning, databaseJob, null)}
          failure={
            databaseJob?.errorCode
              ? { body: <FailureBody code={databaseJob.errorCode} /> }
              : undefined
          }
        />
      </div>
      {perTenant ? (
        <div data-testid="tenant-stepper-resources">
          <ProvisioningPanel
            instanceId={null}
            instanceName={tenant.displayName}
            title="Azure resources"
            headingLevel="h3"
            testId="tenant-provisioning-resources"
            source={asPanelSource(provisioning, resourcesJob, null)}
            stepLabels={TENANT_RESOURCE_STEP_LABELS}
            failure={
              resourcesJob?.errorCode
                ? { body: <FailureBody code={resourcesJob.errorCode} />, actions: retryButton }
                : { body: "The job stopped.", actions: retryButton }
            }
          />
        </div>
      ) : null}
      {showApplyNow ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3"
          data-testid="tenant-resources-apply"
        >
          <SkyText size={200} color="muted" block>
            Deploy this tenant's Azure resources template again, for example after a release changes
            it.
          </SkyText>
          <SkyButton
            appearance="secondary"
            size="small"
            onClick={() => setConfirmApply(true)}
            testId="tenant-resources-apply-now"
          >
            Apply now
          </SkyButton>
        </div>
      ) : null}
      <SkyDialog
        open={confirmApply}
        onOpenChange={setConfirmApply}
        title={`Apply now for ${tenant.displayName}`}
        description={
          tenant.resources?.status === "failed"
            ? APPLY_CONFIRM_FAILED_SENTENCE
            : APPLY_CONFIRM_SENTENCE
        }
        size="small"
        testId="tenant-resources-apply-dialog"
        // Closes when the request settles, so the button's loading state stays in view.
        onBeforeClose={(reason) => reason !== "primaryAction"}
        primaryAction={{ label: "Apply now", onClick: applyNow, loading: apply.isPending }}
        secondaryAction={{ label: "Cancel", onClick: () => setConfirmApply(false) }}
      >
        <SkyText as="p" size={200} color="muted" block>
          The change is recorded on the audit trail with your name.
        </SkyText>
      </SkyDialog>
    </div>
  );
}

export function TenantResourcesDrawer({
  tenant,
  onClose,
}: {
  /** The tenant whose resources are open; null closes the drawer. */
  tenant: TenantView | null;
  onClose: () => void;
}) {
  if (!tenant?.resources) return null;
  return (
    <SkyDrawer
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      position="end"
      size="large"
      headerTitle={`Azure resources of ${tenant.displayName}`}
      subtitle="What this tenant has in Azure and how far its set-up is."
      testId="tenant-resources-drawer"
      footerButtons={[{ label: "Close", appearance: "subtle", onClick: onClose }]}
    >
      <div className="flex flex-col gap-6">
        <ResourceFacts tenant={tenant} />
        <Steppers tenant={tenant} />
      </div>
    </SkyDrawer>
  );
}
